import { useState } from 'react';
import { getOrderApi } from '../../api/orderApi';
import { Link } from 'react-router-dom';
import imgUrl from '../../utils/imgUrl';

const STATUS_STEPS = ['confirmed', 'processing', 'shipped', 'delivered'];
const STATUS_ICONS  = { confirmed: '✅', processing: '⚙️', shipped: '🚚', delivered: '📦', pending: '⏳', cancelled: '❌' };

export default function TrackOrder() {
  const [orderId, setOrderId]   = useState('');
  const [order, setOrder]       = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderId.trim()) return;
    setLoading(true);
    setError('');
    setOrder(null);
    try {
      const res = await getOrderApi(orderId.trim());
      setOrder(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Order not found. Please check your Order ID.');
    } finally { setLoading(false); }
  };

  const stepIdx = order ? STATUS_STEPS.indexOf(order.status) : -1;

  return (
    <div className="page-top">
      {/* Hero */}
      <div style={S.hero}>
        <div style={S.heroBg}>TRACK</div>
        <div style={S.heroContent}>
          <p className="sec-label" style={{ color: 'var(--gold)', marginBottom: '10px' }}>Real-time Updates</p>
          <h1 style={S.heroTitle}>TRACK YOUR ORDER</h1>
          <p style={S.heroSub}>Enter your Order ID to get live updates on your shipment.</p>
        </div>
      </div>

      {/* Track Form */}
      <div style={S.formSection}>
        <div style={S.formCard}>
          <h2 style={S.formTitle}>ENTER ORDER DETAILS</h2>
          <form onSubmit={handleTrack} style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <input style={S.inp} placeholder="Order ID (e.g. BL-2024-XXXXX)" value={orderId} onChange={(e) => setOrderId(e.target.value)} required />
            <button type="submit" style={S.trackBtn} disabled={loading}>
              {loading ? 'TRACKING...' : 'TRACK →'}
            </button>
          </form>
          <p style={{ fontSize: '12px', color: '#aaa', marginTop: '10px' }}>
            💡 Find your Order ID in your confirmation email or{' '}
            <Link to="/dashboard" style={{ color: 'var(--red)', fontWeight: 600 }}>your dashboard</Link>.
          </p>
          {error && (
            <div style={S.errorBox}>
              <p style={{ fontWeight: 600, marginBottom: '4px' }}>Order Not Found</p>
              <p style={{ fontSize: '12px', color: '#888' }}>{error}</p>
            </div>
          )}
        </div>

        {/* Result */}
        {order && (
          <div style={S.result}>
            <div style={S.resultHeader}>
              <div>
                <p style={S.orderId}>Order #{order.id.slice(0, 8).toUpperCase()}</p>
                <p style={{ fontSize: '13px', color: '#888' }}>
                  Placed on {new Date(order.placed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={S.orderTotal}>₹{order.total_amount}</p>
                <span style={{ ...S.statusBadge, background: order.status === 'delivered' ? 'var(--green)' : order.status === 'cancelled' ? 'var(--red)' : 'var(--gold)', color: '#fff' }}>
                  {STATUS_ICONS[order.status]} {order.status.toUpperCase()}
                </span>
              </div>
            </div>

            {/* Progress Bar */}
            {order.status !== 'cancelled' && (
              <div style={S.progressSection}>
                <div style={S.progressBar}>
                  {STATUS_STEPS.map((step, i) => (
                    <div key={step} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', position: 'relative' }}>
                      {i > 0 && <div style={{ ...S.progressLine, background: i <= stepIdx ? 'var(--green)' : 'var(--border)' }} />}
                      <div style={{ ...S.progressDot, background: i <= stepIdx ? 'var(--green)' : 'var(--border)', border: `3px solid ${i === stepIdx ? 'var(--black)' : 'transparent'}` }}>
                        {i <= stepIdx && <span style={{ fontSize: '10px' }}>✓</span>}
                      </div>
                      <p style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: i <= stepIdx ? 'var(--black)' : '#bbb', textAlign: 'center' }}>
                        {step}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Items */}
            {order.items && order.items.length > 0 && (
              <div style={S.itemsSection}>
                <p style={S.sectionLabel}>ORDER ITEMS</p>
                {order.items.map((item, i) => (
                  <div key={i} style={S.orderItem}>
                    <div style={S.itemImg}>
                      {item.image ? <img src={imgUrl(item.image)} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '👕'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontWeight: 600, marginBottom: '2px' }}>{item.name}</p>
                      <p style={{ fontSize: '12px', color: '#888' }}>Qty: {item.quantity}</p>
                    </div>
                    <p style={{ fontWeight: 700 }}>₹{(item.unit_price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Help Section */}
      <div style={S.helpSection}>
        <p style={S.helpTitle}>NEED MORE HELP?</p>
        <div style={S.helpGrid}>
          {[
            { icon: '↩️', title: 'Returns & Exchange', sub: 'Not satisfied? 15-day hassle-free returns.', to: '/returns' },
            { icon: '💬', title: 'Contact Support', sub: 'Our team replies within 24 hours.', to: '/contact' },
            { icon: '❓', title: 'FAQs', sub: 'Find answers to common questions.', to: '/faqs' },
          ].map((h) => (
            <Link key={h.to} to={h.to} style={S.helpCard}>
              <span style={{ fontSize: '28px', display: 'block', marginBottom: '10px' }}>{h.icon}</span>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: '14px', letterSpacing: '1.5px', marginBottom: '6px' }}>{h.title}</p>
              <p style={{ fontSize: '12px', color: '#888' }}>{h.sub}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

const S = {
  hero:          { background: 'var(--black)', padding: '60px 56px 52px', position: 'relative', overflow: 'hidden', borderBottom: '4px solid var(--gold)' },
  heroBg:        { position: 'absolute', right: '-20px', top: '50%', transform: 'translateY(-50%)', fontFamily: 'var(--font-display)', fontSize: 'clamp(80px,15vw,200px)', letterSpacing: '-4px', color: 'rgba(255,255,255,.04)', lineHeight: 1, userSelect: 'none' },
  heroContent:   { position: 'relative', zIndex: 1 },
  heroTitle:     { fontFamily: 'var(--font-display)', fontSize: 'clamp(32px,5vw,56px)', letterSpacing: '2px', color: '#fff', lineHeight: 1, marginBottom: '14px' },
  heroSub:       { fontSize: '14px', color: 'rgba(255,255,255,.5)', lineHeight: 1.8, maxWidth: '420px' },
  formSection:   { padding: '48px 56px', maxWidth: '900px', margin: '0 auto' },
  formCard:      { background: '#fff', border: '1px solid var(--border)', padding: '32px', marginBottom: '24px' },
  formTitle:     { fontFamily: 'var(--font-display)', fontSize: '18px', letterSpacing: '2px' },
  inp:           { flex: 1, border: '1.5px solid var(--border)', background: 'var(--offwhite)', padding: '12px 16px', fontFamily: 'var(--font-body)', fontSize: '14px', outline: 'none', minWidth: 0 },
  trackBtn:      { padding: '12px 28px', background: 'var(--red)', color: '#fff', border: 'none', fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer', flexShrink: 0 },
  errorBox:      { background: '#fff5f5', border: '1px solid #fecaca', borderLeft: '3px solid var(--red)', padding: '14px', marginTop: '14px' },
  result:        { background: '#fff', border: '1px solid var(--border)' },
  resultHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '24px', borderBottom: '1px solid var(--border)' },
  orderId:       { fontFamily: 'var(--font-display)', fontSize: '20px', letterSpacing: '2px', marginBottom: '4px' },
  orderTotal:    { fontFamily: 'var(--font-display)', fontSize: '20px', letterSpacing: '1px', color: 'var(--black)', marginBottom: '6px' },
  statusBadge:   { display: 'inline-block', fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', padding: '4px 10px' },
  progressSection:{ padding: '28px 24px' },
  progressBar:   { display: 'flex', alignItems: 'flex-start', position: 'relative' },
  progressLine:  { position: 'absolute', top: '12px', right: '50%', left: '-50%', height: '2px', zIndex: 0 },
  progressDot:   { width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#fff', flexShrink: 0, position: 'relative', zIndex: 1 },
  itemsSection:  { padding: '0 24px 24px', borderTop: '1px solid var(--border)' },
  sectionLabel:  { fontFamily: 'var(--font-display)', fontSize: '12px', letterSpacing: '2.5px', color: '#888', padding: '14px 0 10px' },
  orderItem:     { display: 'flex', alignItems: 'center', gap: '14px', padding: '10px 0', borderBottom: '1px solid var(--border)' },
  itemImg:       { width: '50px', height: '64px', background: 'var(--light)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', fontSize: '20px' },
  helpSection:   { padding: '56px', background: 'var(--offwhite)', borderTop: '1px solid var(--border)' },
  helpTitle:     { fontFamily: 'var(--font-display)', fontSize: '16px', letterSpacing: '3px', textAlign: 'center', marginBottom: '28px', color: '#888' },
  helpGrid:      { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '16px', maxWidth: '700px', margin: '0 auto' },
  helpCard:      { background: '#fff', border: '1px solid var(--border)', padding: '24px', textDecoration: 'none', color: 'inherit', display: 'block', transition: 'border-color .2s' },
};
